/**
 * 소개영상 조립기 — 상품의 실제 사진 + AI 나레이션 → MP4 (브라우저 안에서)
 *
 * 서버 없이 브라우저만으로 mp4 를 만든다:
 *   Canvas 에 장면(사진 + 카메라 움직임 + 자막)을 한 프레임씩 그린다
 *     — 장면에 AI 클립(Veo 로 사진을 움직인 mp4)이나 사용자가 올린 동영상이 있으면 사진 대신 그 클립을 깔고
 *       자막·전환·음악은 똑같이 얹는다 (videoBlobs). 클립이 여럿이면 사진처럼 문장 경계에서 컷한다.
 *     — 슬라이드쇼처럼 보이지 않게: 컷은 문장 경계에, 전환은 푸시/줌스루/페이드/딥투블랙을 섞고,
 *       카메라 움직임은 사진마다 다르게, 특징 장면은 콜라주 + 포인트 칩, 일차 장면은 가끔 2분할,
 *       제목은 단어별로 뜨고, 마지막에 톤별 색감(비네팅·틴트·필름 입자)을 입힌다 (LOOKS)
 *   → WebCodecs VideoEncoder(H.264) / AudioEncoder(AAC, 안 되면 Opus) 로 압축한다
 *   → mp4-muxer 가 mp4 컨테이너로 묶는다.
 *
 * 쓰는 순서:
 *   prepareProject(...)     사진 내려받기·나레이션 리샘플·타임라인·오디오 트랙까지 한 번에 준비 → project
 *   createPreview(project)  캔버스에서 소리와 함께 실시간 재생 (인코딩 없이 확인)
 *   encodeProject(project)  mp4 Blob
 *   releaseProject(project) 클립용 <video> 등 정리
 *
 * MediaRecorder(webm) 를 쓰지 않는 이유: 만든 영상이 상세페이지에 실려 고객 휴대폰에서 재생되는데
 * webm 은 iOS Safari 에서 안 돌아간다. mp4(H.264) 가 유일하게 어디서나 열린다.
 * ffmpeg.wasm 을 쓰지 않는 이유: 30MB 다운로드 + 30초 720p 인코딩에 수 분이 걸린다.
 * WebCodecs 는 브라우저 내장 인코더라 몇 초~수십 초면 끝난다. 대신 Chrome/Edge 최신 버전이 필요하다.
 */

/* global VideoEncoder, AudioEncoder, VideoFrame, AudioData */
// (WebCodecs 전역은 CRA 의 eslint 브라우저 목록에 없을 수 있어 여기서 선언한다)

import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { trimSilence, timeStretch, generateMusic, synthWhoosh, buildDuckingCurve, softClip } from './audioTools';
import { buildRouteMap, drawRouteMap } from './routeMap';

/** 출력 형식. 16:9 는 상세페이지용, 9:16 은 릴스·쇼츠용, 1:1 은 피드용. */
export const ASPECTS = {
  '16:9': { width: 1280, height: 720, label: '가로 16:9', hint: '상세페이지·유튜브' },
  '9:16': { width: 720, height: 1280, label: '세로 9:16', hint: '릴스·쇼츠·스토리' },
  '1:1': { width: 960, height: 960, label: '정사각 1:1', hint: '인스타 피드' },
};
export const VIDEO_FPS = 30;
/**
 * 완성 mp4 의 최대 크기(바이트). 상세페이지 업로드 API(uploadS3)가 파일을 base64 JSON 으로 받아 요청 크기에 상한이 있고,
 * 고객 휴대폰에서 바로 열려야 해서 20MB 를 넘지 않게 한다. 영상 비트레이트를 길이에 맞춰 이 안에 들어오도록 정한다.
 */
export const MAX_OUTPUT_BYTES = 20 * 1024 * 1024;
/** 영상 비트레이트 상한(bps). 사진 슬라이드는 움직임이 적어 2.8Mbps 면 720p 가 충분히 선명하다 (30초 ≈ 10~11MB). */
const VIDEO_BITRATE_MAX = 2_800_000;
/** 영상 비트레이트 하한(bps). 이보다 낮추면 720p 가 뭉개져 차라리 크기 초과를 알리는 게 낫다. */
const VIDEO_BITRATE_MIN = 500_000;
/** 인코더에 넣는 오디오 샘플레이트. AAC/Opus 모두 48k 를 가장 확실히 지원한다. */
const AUDIO_RATE = 48000;

const FONT_STACK = "'Pretendard', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif";
/** 나레이션이 끝난 뒤 다음 장면으로 넘어가기 전 숨 고르는 시간(초). 길면 늘어진다. */
const SCENE_TAIL_SEC = 0.45;
/** 배경음악 기본 음량 (사용자 슬라이더 1.0 일 때). 나레이션 밑에 깔리는 게 목적이라 낮다. */
const MUSIC_BASE_GAIN = 0.45;
/** 나레이션 중 배경음악을 이만큼으로 낮춘다 (덕킹) */
const MUSIC_DUCK_TO = 0.35;
/** 장면 전환 효과음 음량 */
const SFX_GAIN = 0.32;
/** 장면 최소 길이(초). 나레이션이 짧아도 화면이 휙 지나가지 않게 */
const SCENE_MIN_SEC = 2.5;
/** 한 장면에서 사진 한 장이 머무는 최소 시간(초). 이보다 짧아지면 사진 수를 줄인다. */
const SHOT_MIN_SEC = 2.4;
/** AI 클립 자체 소리를 나레이션 밑에 깔 때의 음량 */
const CLIP_BED_GAIN = 0.3;
/** 카메라 최대 확대 배율(cameraAt 의 최대 z 보다 커야 한다). 이만큼 여유를 두고 미리 그려 두면 매 프레임 원본을 다시 줄이지 않아도 된다. */
const ZOOM_MAX = 1.25;
/** 일차 장면 앞에 붙는 미니맵 전환(지도 위로 선이 다음 도시까지 나아가는) 길이(초) */
const MAP_LEG_SEC = 1.3;

/** 이 브라우저에서 영상 조립이 가능한가 (Chrome/Edge 94+ 계열) */
export function isVideoBuildSupported() {
  if (typeof window === 'undefined') return false;
  return !!(window.VideoEncoder && window.VideoFrame && window.AudioEncoder && window.AudioData && window.OfflineAudioContext);
}

// ── 오디오 유틸 ──────────────────────────────────────────────────

/** Gemini TTS 가 주는 base64 PCM(16bit little-endian mono) → Float32Array(-1..1) */
export function pcm16Base64ToFloat32(base64) {
  const bin = atob(base64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  // 홀수 바이트가 섞여 오면 마지막 바이트는 버린다
  const view = new DataView(bytes.buffer, 0, bytes.length - (bytes.length % 2));
  const out = new Float32Array(view.byteLength / 2);
  for (let i = 0; i < out.length; i++) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
}

/** AudioBuffer 를 만든다 (AudioContext 는 호출자가 관리) */
export function floatToAudioBuffer(ctx, samples, sampleRate) {
  const buf = ctx.createBuffer(1, samples.length, sampleRate);
  buf.copyToChannel(samples, 0);
  return buf;
}

/** 다른 샘플레이트의 PCM 을 AUDIO_RATE 로 리샘플한다 (OfflineAudioContext 에 맡긴다) */
async function resampleTo48k(samples, sampleRate) {
  if (!samples || !samples.length) return new Float32Array(0);
  if (sampleRate === AUDIO_RATE) return samples;
  const length = Math.ceil((samples.length * AUDIO_RATE) / sampleRate);
  const ctx = new OfflineAudioContext(1, length, AUDIO_RATE);
  const src = ctx.createBufferSource();
  src.buffer = floatToAudioBuffer(ctx, samples, sampleRate);
  src.connect(ctx.destination);
  src.start(0);
  const rendered = await ctx.startRendering();
  return rendered.getChannelData(0);
}

// ── 이미지 유틸 ──────────────────────────────────────────────────

/**
 * 사진을 CORS 모드로 받아 ImageBitmap 으로 만든다.
 *
 * 왜 <img crossOrigin> 을 바로 쓰지 않나: S3(kr.object.iwinv.kr) 는 요청에 Origin 헤더가 있을 때만
 * Access-Control-Allow-Origin 을 붙인다. 그런데 이 화면의 <img> 들이 이미 같은 URL 을 Origin 없이
 * 받아 캐시해 두었기 때문에, 나중에 crossOrigin 으로 다시 요청하면 브라우저가 헤더 없는 캐시 응답을
 * 돌려주고 CORS 에 걸린다. cache:'reload' 로 캐시를 건너뛰어 Origin 이 붙은 새 요청을 보낸다.
 *
 * @param {Map<string, ImageBitmap|null>} [cache]  같은 세션에서 같은 URL 을 다시 받지 않도록
 */
export async function loadImage(url, cache) {
  if (!url) return null;
  if (cache?.has(url)) return cache.get(url);
  let result = null;
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'reload' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    result = await createImageBitmap(await res.blob());
  } catch {
    // fetch 가 막히면(드물게 리다이렉트 등) URL 을 살짝 바꿔 캐시를 피한 <img> 로 한 번 더 시도한다
    result = await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      // data:/blob: 은 캐시 문제가 없고, 꼬리를 붙이면 주소가 깨진다
      img.src = /^(data|blob):/.test(url) ? url : `${url}${url.includes('?') ? '&' : '?'}_cors=${Date.now()}`;
    });
  }
  cache?.set(url, result);
  return result;
}

/** AI 에게 사진을 보여줄 때 쓰는 작은 썸네일 (base64 JPEG, data: 접두사 없음). 실패하면 null. */
export async function makeThumbnailBase64(url, cache, size = 224) {
  const img = await loadImage(url, cache);
  if (!img) return null;
  const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
  const scale = Math.min(1, size / Math.max(iw, ih));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(iw * scale));
  c.height = Math.max(1, Math.round(ih * scale));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  const dataUrl = c.toDataURL('image/jpeg', 0.6);
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

/**
 * Veo 이미지→영상의 첫 프레임. 사진을 출력 비율(16:9 → 1280×720, 9:16 → 720×1280)로 가운데 crop-fill 해서
 * JPEG base64(data: 접두사 없음)로 돌려준다. 흐린 배경 위에 얹는 방식을 안 쓰는 이유: Veo 가 흐린 테두리를
 * "풍경" 으로 읽고 이상한 것을 그려 넣는다. 잘라내는 쪽이 지시가 분명하다. 실패하면 null.
 * @param {'16:9'|'9:16'} aspect
 */
export async function makeFirstFrameBase64(url, cache, aspect = '16:9') {
  const img = await loadImage(url, cache);
  if (!img) return null;
  const { width, height } = ASPECTS[aspect] || ASPECTS['16:9'];
  const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
  const c = document.createElement('canvas');
  c.width = width; c.height = height;
  const ctx = c.getContext('2d');
  const cover = Math.max(width / iw, height / ih);
  ctx.drawImage(img, (width - iw * cover) / 2, (height - ih * cover) / 2, iw * cover, ih * cover);
  const dataUrl = c.toDataURL('image/jpeg', 0.85);
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

/**
 * 사진을 칸 비율로 미리 그려둔 캔버스 (카메라 움직임 여유분 포함).
 * 비율이 칸과 많이 다르면(세로 사진을 가로 화면에 등) 잘라내지 않고, 흐린 배경 위에 원본을 얹는다.
 * fill 이면 무조건 잘라서 꽉 채운다 — 2분할·콜라주의 작은 칸은 흐린 배경보다 잘라낸 쪽이 낫다.
 */
function prerenderCover(img, width, height, fill = false) {
  const bw = Math.round(width * ZOOM_MAX);
  const bh = Math.round(height * ZOOM_MAX);
  const c = document.createElement('canvas');
  c.width = bw; c.height = bh;
  const ctx = c.getContext('2d');
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const ratio = (iw / ih) / (bw / bh);
  const cover = Math.max(bw / iw, bh / ih);
  if (fill || (ratio > 0.75 && ratio < 1.33)) {
    // 비율이 비슷하면(또는 fill) 그냥 꽉 채운다
    ctx.drawImage(img, (bw - iw * cover) / 2, (bh - ih * cover) / 2, iw * cover, ih * cover);
    return c;
  }
  // 1) 흐린 배경: 작은 캔버스에 그려 블러(빠르다) → 확대
  const small = document.createElement('canvas');
  small.width = 64; small.height = Math.max(1, Math.round((64 * bh) / bw));
  const sctx = small.getContext('2d');
  const sc = Math.max(small.width / iw, small.height / ih);
  sctx.filter = 'blur(3px)';
  sctx.drawImage(img, (small.width - iw * sc) / 2, (small.height - ih * sc) / 2, iw * sc, ih * sc);
  ctx.filter = 'blur(24px)';
  ctx.drawImage(small, -bw * 0.05, -bh * 0.05, bw * 1.1, bh * 1.1);
  ctx.filter = 'none';
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.fillRect(0, 0, bw, bh);
  // 2) 원본을 contain 으로 가운데에
  const contain = Math.min((bw * 0.92) / iw, (bh * 0.92) / ih);
  const dw = iw * contain, dh = ih * contain;
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 30;
  ctx.drawImage(img, (bw - dw) / 2, (bh - dh) / 2, dw, dh);
  return c;
}

/**
 * 클립(mp4 Blob — Veo 가 만든 것이든 사용자가 올린 동영상이든)을 장면 재료로 준비한다: 프레임을 뽑을 <video> 와,
 * 나레이션 밑에 깔 클립 자체 소리(48k mono). 소리 디코딩이 실패해도 영상은 쓴다.
 */
async function loadClip(blob, { decodeAudio = true } = {}) {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;
  await new Promise((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('클립을 열 수 없습니다 (브라우저가 재생 못 하는 형식).'));
  });
  if (!Number.isFinite(video.duration) || video.duration <= 0) { URL.revokeObjectURL(url); throw new Error('클립 길이를 알 수 없습니다.'); }
  // 첫 프레임을 실제로 디코딩해 둔다 (loadedmetadata 만으로는 drawImage 가 검은 화면일 수 있다)
  await seekVideo(video, 0.001);
  let audio = null;
  if (!decodeAudio) return { video, url, duration: video.duration, audio };
  try {
    const buf = await blob.arrayBuffer();
    const ctx = new OfflineAudioContext(1, 1, AUDIO_RATE);
    const decoded = await ctx.decodeAudioData(buf);
    const n = decoded.length;
    const mono = new Float32Array(n);
    for (let c = 0; c < decoded.numberOfChannels; c++) {
      const ch = decoded.getChannelData(c);
      for (let i = 0; i < n; i++) mono[i] += ch[i] / decoded.numberOfChannels;
    }
    audio = { samples: await resampleTo48k(mono, decoded.sampleRate), sampleRate: AUDIO_RATE };
  } catch { /* 소리 없는 클립이거나 디코딩 불가 — 영상만 쓴다 */ }
  return { video, url, duration: video.duration, audio };
}

/**
 * <video> 를 원하는 시각으로 옮기고 프레임이 준비될 때까지 기다린다.
 * 프레임이 아직 하나도 디코딩되지 않았으면(readyState < 2) 같은 시각이라도 seek 를 강제한다 — 안 그러면 첫 프레임이 검게 나온다.
 * 이미 놓아 버린(src 없는) 요소나 멈춘 디코더 때문에 영원히 기다리지 않도록 2초 상한을 둔다.
 */
function seekVideo(video, sec) {
  if (!Number.isFinite(video.duration) || video.duration <= 0) return Promise.resolve();
  const target = Math.max(0, Math.min(sec, Math.max(0, video.duration - 0.05)));
  if (video.readyState >= 2 && Math.abs(video.currentTime - target) < 1 / 120) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); video.removeEventListener('seeked', done); video.removeEventListener('error', done); resolve(); };
    const timer = setTimeout(done, 2000);
    video.addEventListener('seeked', done);
    video.addEventListener('error', done);
    // 같은 시각이면 seeked 가 안 올 수 있어 살짝 다른 값으로
    video.currentTime = Math.abs(video.currentTime - target) < 1 / 120 ? target + 1 / 60 : target;
  });
}

// ── 자막 ─────────────────────────────────────────────────────────

/**
 * 나레이션 문장을 자막 조각으로 나누고, 글자 수 비율로 시간을 배분한다.
 * (TTS 의 단어별 타이밍은 받을 수 없어서 근사치다. 문장 단위라 어긋나도 크게 티 나지 않는다.)
 */
function planSubtitles(text, start, narrSec, maxChars) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean || narrSec <= 0) return [];
  // 1) 문장으로 나누고
  const sentences = clean.split(/(?<=[.!?。])\s+/).filter(Boolean);
  // 2) 긴 문장은 띄어쓰기 기준으로 maxChars 안에 들어가게 다시 나눈다
  const chunks = [];
  for (const sentence of sentences) {
    if (sentence.length <= maxChars) { chunks.push(sentence); continue; }
    let cur = '';
    for (const word of sentence.split(' ')) {
      if ((cur + ' ' + word).trim().length > maxChars && cur) { chunks.push(cur); cur = word; } else cur = (cur + ' ' + word).trim();
    }
    if (cur) chunks.push(cur);
  }
  const total = chunks.reduce((n, c) => n + c.length, 0) || 1;
  let cursor = start;
  return chunks.map((c) => {
    const dur = (c.length / total) * narrSec;
    const item = { text: c, start: cursor, end: cursor + dur };
    cursor += dur;
    return item;
  });
}

// ── 타임라인 ─────────────────────────────────────────────────────

/**
 * 카메라 움직임 종류. 사진마다 다른 것을 골라 "같은 줌인만 반복" 을 피한다.
 *   pushIn/pullOut — 줌, panRight/panLeft — 옆으로 훑기, diagIn — 대각선으로 다가가기, drift — 살짝 기울며 떠다니기
 */
const MOVES = ['pushIn', 'panRight', 'pullOut', 'diagIn', 'panLeft', 'drift'];
const moveFor = (shotIdx, sceneIdx) => MOVES[(sceneIdx * 2 + shotIdx * 3) % MOVES.length];

/**
 * 장면 사이 전환. 구간 경계(인트로→특징, →마무리)는 딥투블랙으로 호흡을 끊고, 종류가 바뀌면 푸시,
 * 같은 종류(일차→일차)는 푸시·줌스루·페이드를 번갈아 쓴다. 전부 페이드였을 때의 슬라이드쇼 느낌을 없애는 핵심.
 */
function transitionBetween(prev, next) {
  if (!prev) return { transition: 'fade', transSec: 0.4 };
  if (prev.kind !== next.kind) {
    if (next.kind === 'closing' || prev.kind === 'intro') return { transition: 'dip', transSec: 0.7 };
    return { transition: 'push', transSec: 0.6 };
  }
  const pick = ['push', 'zoom', 'fade', 'zoom'][next.index % 4];
  return { transition: pick, transSec: pick === 'fade' ? 0.5 : 0.6 };
}

/** 나레이션의 문장 시작 시각들 (첫 값은 장면 시작). 자막과 같은 근사(글자 수 비율)를 쓴다. */
function sentenceStarts(text, start, narrSec) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean || narrSec <= 0) return [start];
  const sentences = clean.split(/(?<=[.!?。])\s+/).filter(Boolean);
  const total = sentences.reduce((n, s) => n + s.length, 0) || 1;
  const starts = [];
  let cursor = start;
  for (const s of sentences) { starts.push(cursor); cursor += (s.length / total) * narrSec; }
  return starts;
}

/**
 * 전체화면 장면의 샷(사진 교체) 계획. 컷은 문장이 바뀌는 순간에 둔다 — 말과 그림이 같이 바뀌어야 편집된 느낌이 난다.
 * 문장 경계가 모자라면 균등 분할점을 쓴다. 장면 안 교체는 0.18초 소프트컷.
 */
function planShots(scene, start, duration, narrSec, narrStart = start) {
  const images = scene.images;
  const first = { transition: scene.transition, transSec: scene.transSec };
  if (!images.length) return [{ image: null, start, end: start + duration, move: 'drift', ...first }];
  const maxByTime = Math.floor(duration / SHOT_MIN_SEC) || 1;
  const count = Math.max(1, Math.min(images.length, maxByTime));
  const margin = SHOT_MIN_SEC * 0.8;
  const bounds = sentenceStarts(scene.narration, narrStart, narrSec).slice(1)
    .filter((b) => b - start >= margin && start + duration - b >= margin);
  const cuts = [];
  for (let i = 1; i < count; i++) {
    const target = start + (duration * i) / count;
    let best = null;
    let bestD = Infinity;
    for (const b of bounds) {
      const d = Math.abs(b - target);
      if (d < bestD && !cuts.includes(b)) { bestD = d; best = b; }
    }
    cuts.push(best != null && bestD < duration / count / 2 ? best : target);
  }
  cuts.sort((a, b) => a - b);
  const edges = [start, ...cuts, start + duration];
  return edges.slice(0, -1).map((s, i) => ({
    image: images[i],
    start: s,
    end: edges[i + 1],
    move: moveFor(i, scene.index),
    ...(i === 0 ? first : { transition: 'fade', transSec: 0.18 }),
  }));
}

/**
 * 클립 장면의 샷 계획 — planShots 와 같은 규칙(문장 경계 컷)이되 사진 대신 클립을 하나씩 놓는다.
 * 클립은 샷 시작에서 처음부터 재생되고, 샷이 클립보다 길면 마지막 프레임에서 멈춘다(seekVideo 가 끝에서 잡아 준다).
 * 그래서 나레이션이 클립보다 길어도 영상이 늘어나지 않고, 짧으면 클립 뒷부분을 버린다 — 레퍼런스 홍보영상들이 2~4초 컷을 쓰는 이유다.
 */
function planClipShots(scene, start, duration, narrSec, narrStart = start) {
  const clips = scene.clips;
  const first = { transition: scene.transition, transSec: scene.transSec };
  const maxByTime = Math.floor(duration / SHOT_MIN_SEC) || 1;
  const count = Math.max(1, Math.min(clips.length, maxByTime));
  const margin = SHOT_MIN_SEC * 0.8;
  const bounds = sentenceStarts(scene.narration, narrStart, narrSec).slice(1)
    .filter((b) => b - start >= margin && start + duration - b >= margin);
  const cuts = [];
  for (let i = 1; i < count; i++) {
    const target = start + (duration * i) / count;
    let best = null;
    let bestD = Infinity;
    for (const b of bounds) {
      const d = Math.abs(b - target);
      if (d < bestD && !cuts.includes(b)) { bestD = d; best = b; }
    }
    cuts.push(best != null && bestD < duration / count / 2 ? best : target);
  }
  cuts.sort((a, b) => a - b);
  const edges = [start, ...cuts, start + duration];
  return edges.slice(0, -1).map((st, i) => ({
    clip: clips[i],
    image: null,
    start: st,
    end: edges[i + 1],
    move: 'none',
    ...(i === 0 ? first : { transition: 'fade', transSec: 0.18 }),
  }));
}

/**
 * 일차 장면이 지도의 어느 구간을 보여줄지. 이 장면의 일차에 방문하는 점들이 있고 그 앞에 지나온 점이 있으면
 * (직전 점 → 이 장면의 마지막 점) 구간을 돌려준다. 첫 방문지나 매핑이 안 되는 날은 null.
 */
function legFor(map, scene) {
  if (!map) return null;
  const days = new Set((scene.dayNumbers || []).map(Number).filter((n) => n > 0));
  if (!days.size) return null;
  const idxs = map.points.map((p, i) => (days.has(p.day) ? i : -1)).filter((i) => i >= 0);
  if (!idxs.length) return null;
  const toIdx = Math.max(...idxs);
  const fromIdx = Math.min(...idxs) - 1;
  return fromIdx >= 0 ? { fromIdx, toIdx } : null;
}

/**
 * 장면 목록 + 각 장면의 나레이션 길이로 타임라인을 짠다.
 * 각 장면은 "나레이션 길이 + 숨 고르기" 만큼 머문다. 그래서 전체 길이는 대본 길이에서 자연스럽게 나온다.
 *
 * 레이아웃: 경로 장면은 지도(선이 순서대로 그려짐), 특징 장면은 사진 콜라주(포인트 칩이 하나씩 뜬다),
 * 일차 장면은 앞에 미니맵 전환이 붙을 수 있고 세 번에 한 번 2분할, 나머지는 전체화면.
 */
function planTimeline(scenes, subtitleMaxChars, map) {
  let cursor = 0;
  let prev = null;
  let dayIdx = 0;
  return scenes.map((scene) => {
    const narrSec = scene.audio ? scene.audio.samples.length / scene.audio.sampleRate : 0;
    // 클립이 있어도 장면 길이는 나레이션이 정한다 (클립이 길면 뒤를 버리고, 짧으면 마지막 프레임에서 멈춘다)
    const duration = Math.max(SCENE_MIN_SEC, narrSec + SCENE_TAIL_SEC);
    const start = cursor;
    cursor += duration;
    const subtitles = planSubtitles(scene.narration, start, narrSec, subtitleMaxChars);
    const tr = transitionBetween(prev, scene);
    // 화면이 아직 넘어오는 중(검은 화면·밀려 들어오는 중)에 글자가 먼저 뜨지 않게 글자 등장을 늦춘다
    const textDelay = tr.transition === 'dip' ? tr.transSec * 0.5 : tr.transition === 'fade' ? 0 : tr.transSec * 0.7;
    const base = { ...scene, ...tr, start, end: cursor, duration, narrSec, subtitles, textDelay };

    let shots;
    const leg = scene.kind === 'day' && duration >= MAP_LEG_SEC + SHOT_MIN_SEC ? legFor(map, scene) : null;
    if (scene.clips.length && leg) {
      // 미니맵 → 줌스루로 클립에 들어간다 (사진 장면과 같은 연출)
      const clipStart = start + MAP_LEG_SEC;
      const rest = planClipShots({ ...base, transition: 'zoom', transSec: 0.5 }, clipStart, cursor - clipStart, narrSec, start);
      shots = [{ layout: 'map', mode: 'leg', map, ...leg, start, end: clipStart, transition: tr.transition, transSec: tr.transSec }, ...rest];
    } else if (scene.clips.length) {
      shots = planClipShots(base, start, duration, narrSec);
    } else if (scene.kind === 'route' && map) {
      shots = [{ layout: 'map', mode: 'full', map, start, end: cursor, transition: tr.transition, transSec: tr.transSec }];
    } else if (leg) {
      // 미니맵(선이 다음 도시로 나아감) → 줌스루로 그날 첫 사진에 들어간다
      const photoStart = start + MAP_LEG_SEC;
      const rest = planShots({ ...base, transition: 'zoom', transSec: 0.5 }, photoStart, cursor - photoStart, narrSec, start);
      shots = [{ layout: 'map', mode: 'leg', map, ...leg, start, end: photoStart, transition: tr.transition, transSec: tr.transSec }, ...rest];
    } else if (scene.kind === 'feature' && scene.images.length >= 2) {
      shots = [{ layout: 'collage', images: scene.images.slice(0, 3), start, end: cursor, moves: scene.images.map((_, i) => moveFor(i, scene.index)), transition: tr.transition, transSec: tr.transSec }];
    } else if (scene.kind === 'day' && scene.images.length >= 2 && dayIdx % 3 === 2 && duration >= SHOT_MIN_SEC * 1.5) {
      shots = [{ layout: 'split', images: scene.images.slice(0, 2), start, end: cursor, moves: ['panRight', 'pullOut'], transition: tr.transition, transSec: tr.transSec }];
    } else {
      shots = planShots(base, start, duration, narrSec);
    }
    if (scene.kind === 'day') dayIdx++;
    const out = { ...base, shots };
    prev = out;
    return out;
  });
}

// ── 그리기 ───────────────────────────────────────────────────────

/** 한글은 단어 단위로 끊기 어려워 글자 단위로 줄을 나눈다. */
function wrapText(ctx, text, maxWidth) {
  const lines = [];
  let line = '';
  for (const ch of String(text || '')) {
    if (ch === '\n') { lines.push(line); line = ''; continue; }
    const test = line + ch;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = ch === ' ' ? '' : ch;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
/** 살짝 넘쳤다 돌아오는 팝 (칩·제목 등장용) */
const overshoot = (t) => { const c = 1.70158; const x = t - 1; return 1 + (c + 1) * x * x * x + c * x * x; };
const clamp01 = (v) => Math.max(0, Math.min(1, v));

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const PALETTES = [
  ['#1e3a8a', '#0ea5e9'],
  ['#065f46', '#34d399'],
  ['#7c2d12', '#fb923c'],
  ['#4c1d95', '#a78bfa'],
  ['#0f172a', '#475569'],
];

/**
 * 톤별 색감. 사진마다 제각각인 색을 한 편의 영상처럼 묶어 준다.
 *   filter — 사진을 그릴 때의 캔버스 필터, tint — 위에 soft-light 로 얹는 색, grain — 필름 입자 세기, vignette — 가장자리 어둡기
 */
const LOOKS = {
  calm: { filter: 'saturate(1.06) contrast(1.02)', tint: 'rgba(255,176,96,0.14)', grain: 0.09, vignette: 0.42 },
  energetic: { filter: 'saturate(1.22) contrast(1.06) brightness(1.03)', tint: 'rgba(255,120,170,0.07)', grain: 0.05, vignette: 0.3 },
  premium: { filter: 'saturate(0.82) contrast(1.1) brightness(0.97)', tint: 'rgba(36,58,110,0.16)', grain: 0.14, vignette: 0.55 },
};
const lookOf = (name) => LOOKS[name] || LOOKS.calm;

/** 필름 입자용 노이즈 타일. 프레임마다 위치만 바꿔 깔면 살아 있는 입자처럼 보인다. */
function makeGrainTile(size = 256) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const data = ctx.createImageData(size, size);
  for (let i = 0; i < data.data.length; i += 4) {
    const v = 96 + Math.random() * 64; // 중간 회색 주변 — overlay 합성에서 밝기를 거의 안 바꾸고 질감만 남긴다
    data.data[i] = v; data.data[i + 1] = v; data.data[i + 2] = v; data.data[i + 3] = 255;
  }
  ctx.putImageData(data, 0, 0);
  return c;
}

/**
 * 카메라 움직임 → 이 순간 미리 그려둔 사진(base)에서 잘라낼 창. p 는 0..1 진행률.
 * 돌려주는 값: { z(배율), ox, oy(-1..1, 창 위치), rot(라디안) }
 */
function cameraAt(move, p) {
  const e = easeInOut(p);
  switch (move) {
    case 'pushIn': return { z: 1 + 0.22 * e, ox: 0, oy: 0, rot: 0 };
    case 'pullOut': return { z: 1.22 - 0.22 * e, ox: 0, oy: 0, rot: 0 };
    case 'panRight': return { z: 1.14, ox: -1 + 2 * e, oy: 0.1, rot: 0 };
    case 'panLeft': return { z: 1.14, ox: 1 - 2 * e, oy: -0.1, rot: 0 };
    case 'diagIn': return { z: 1.06 + 0.16 * e, ox: -0.8 + 0.8 * e, oy: -0.8 + 0.8 * e, rot: 0 };
    case 'drift': return { z: 1.12, ox: 0.3 - 0.6 * e, oy: 0, rot: (-1.2 + 2.4 * e) * (Math.PI / 180) };
    default: return { z: 1, ox: 0, oy: 0, rot: 0 };
  }
}

/**
 * 미리 그려둔 사진(base, 화면 비율 × ZOOM_MAX)을 카메라 움직임에 따라 rect 에 그린다.
 * rect 비율이 화면과 다르면(2분할·콜라주) base 가운데를 그 비율로 잘라 쓴다.
 */
function drawCover(ctx, base, move, p, x, y, w, h, look) {
  const cam = cameraAt(move, p);
  const z = Math.min(ZOOM_MAX, Math.max(1, cam.z));
  // 창 크기: rect 비율을 지키면서 base 안에 들어가는 최대 크기 / z
  let winW = base.width / z;
  let winH = (winW * h) / w;
  if (winH > base.height / z) { winH = base.height / z; winW = (winH * w) / h; }
  const slackX = base.width - winW;
  const slackY = base.height - winH;
  const sx = slackX / 2 + (cam.ox * slackX) / 2;
  const sy = slackY / 2 + (cam.oy * slackY) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.filter = look.filter;
  if (cam.rot) {
    // 기울이면 모서리가 비므로 살짝 키워서 그린다
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(cam.rot);
    ctx.scale(1.04, 1.04);
    ctx.translate(-(x + w / 2), -(y + h / 2));
  }
  ctx.drawImage(base, sx, sy, winW, winH, x, y, w, h);
  ctx.restore();
}

/** 클립(<video>)을 rect 에 cover 로 */
function drawClipCover(ctx, clip, x, y, w, h) {
  const v = clip.video;
  const vw = v.videoWidth || w, vh = v.videoHeight || h;
  const scale = Math.max(w / vw, h / vh);
  const dw = vw * scale, dh = vh * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(v, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

/** 사진이 없을 때의 그라데이션 배경 + 천천히 움직이는 빛 */
function drawGradientBg(ctx, p, x, y, w, h, palette) {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, palette[0]);
  g.addColorStop(1, palette[1]);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
  ctx.save();
  ctx.globalAlpha *= 0.18;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(x + w * (0.25 + 0.5 * p), y + h * 0.45, Math.min(w, h) * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** 2분할·콜라주의 칸 나누기. 짧은 변 기준 간격, 둥근 모서리. */
function collageRects(count, width, height, u) {
  const gap = Math.round(u * 0.014);
  const portrait = height > width;
  if (count === 2) {
    return portrait
      ? [{ x: 0, y: 0, w: width, h: (height - gap) / 2 }, { x: 0, y: (height + gap) / 2, w: width, h: (height - gap) / 2 }]
      : [{ x: 0, y: 0, w: (width - gap) / 2, h: height }, { x: (width + gap) / 2, y: 0, w: (width - gap) / 2, h: height }];
  }
  // 3장: 큰 것 하나 + 작은 것 둘
  if (portrait) {
    const bigH = Math.round(height * 0.58);
    const smallW = (width - gap) / 2;
    return [
      { x: 0, y: 0, w: width, h: bigH },
      { x: 0, y: bigH + gap, w: smallW, h: height - bigH - gap },
      { x: smallW + gap, y: bigH + gap, w: smallW, h: height - bigH - gap },
    ];
  }
  const bigW = Math.round(width * 0.6);
  const smallH = (height - gap) / 2;
  return [
    { x: 0, y: 0, w: bigW, h: height },
    { x: bigW + gap, y: 0, w: width - bigW - gap, h: smallH },
    { x: bigW + gap, y: smallH + gap, w: width - bigW - gap, h: smallH },
  ];
}

/**
 * 샷 하나를 화면 전체(0,0,width,height)에 그린다. 레이아웃에 따라 전체화면 / 2분할 / 콜라주.
 * alpha 는 전환 중 겹칠 때의 투명도.
 */
function drawShot(ctx, shot, t, width, height, alpha, palette, look) {
  const p = clamp01((t - shot.start) / Math.max(0.001, shot.end - shot.start));
  ctx.save();
  ctx.globalAlpha = alpha;
  if (shot.clip) {
    drawClipCover(ctx, shot.clip, 0, 0, width, height);
  } else if (shot.layout === 'map') {
    drawRouteMap(ctx, shot, t, width, height, alpha);
  } else if (shot.layout === 'split' || shot.layout === 'collage') {
    const u = Math.min(width, height);
    const rects = collageRects(shot.images.length, width, height, u);
    ctx.fillStyle = '#0b0b0f';
    ctx.fillRect(0, 0, width, height);
    // 칸이 살짝 시차를 두고 들어온다 (콜라주만)
    rects.forEach((r, i) => {
      const enter = shot.layout === 'collage' ? easeOut(clamp01((t - shot.start - i * 0.12) / 0.45)) : 1;
      if (enter <= 0) return;
      ctx.save();
      ctx.globalAlpha = alpha * enter;
      const slide = (1 - enter) * u * 0.04;
      ctx.translate(0, slide);
      drawCover(ctx, shot.images[i], shot.moves[i] || 'pushIn', p, r.x, r.y, r.w, r.h, look);
      ctx.restore();
    });
  } else if (shot.image) {
    drawCover(ctx, shot.image, shot.move, p, 0, 0, width, height, look);
  } else {
    drawGradientBg(ctx, p, 0, 0, width, height, palette);
  }
  ctx.restore();
}

/**
 * 색감 마무리: 비네팅 → 톤 틴트(soft-light) → 필름 입자(overlay). 글자를 얹기 전에 한 번만.
 */
function drawGrade(ctx, project, t, look) {
  const { width, height } = project;
  // 비네팅
  const r = Math.hypot(width, height) / 2;
  const v = ctx.createRadialGradient(width / 2, height / 2, r * 0.45, width / 2, height / 2, r);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, `rgba(0,0,0,${look.vignette})`);
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, width, height);
  // 틴트
  ctx.save();
  ctx.globalCompositeOperation = 'soft-light';
  ctx.fillStyle = look.tint;
  ctx.fillRect(0, 0, width, height);
  // 입자: 프레임마다 타일 위치를 바꾼다
  if (look.grain > 0 && project.grain) {
    const frame = Math.floor(t * VIDEO_FPS);
    const ox = (frame * 97) % project.grain.width;
    const oy = (frame * 61) % project.grain.height;
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = look.grain;
    const pat = ctx.createPattern(project.grain, 'repeat');
    ctx.translate(-ox, -oy);
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, width + ox, height + oy);
  }
  ctx.restore();
}

/**
 * 자막 띠. 화면 맨 아래에 반투명 상자 + 흰 글씨 + 읽기 진행선. 돌려주는 값은 띠가 차지한 높이(자막이 없으면 0)
 * — 위에 오는 제목 블록이 이만큼 올라가야 한다.
 */
function drawSubtitle(ctx, scene, t, width, height, u, pad) {
  const sub = scene.subtitles.find((s) => t >= s.start && t < s.end);
  if (!sub) return 0;
  ctx.save();
  ctx.font = `500 ${Math.round(u * 0.036)}px ${FONT_STACK}`;
  const lines = wrapText(ctx, sub.text, width - pad * 2 - u * 0.04);
  const lh = Math.round(u * 0.05);
  const boxH = lines.length * lh + Math.round(u * 0.03);
  const boxW = Math.min(width - pad * 2, Math.max(...lines.map((l) => ctx.measureText(l).width)) + u * 0.05);
  const y0 = height - pad * 0.6 - boxH;
  const x0 = (width - boxW) / 2;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  roundRect(ctx, x0, y0, boxW, boxH, u * 0.012);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((line, i) => ctx.fillText(line, width / 2, y0 + Math.round(u * 0.015) + lh * (i + 0.5)));
  // 읽기 진행선: 이 조각이 읽히는 동안 왼쪽에서 오른쪽으로 찬다
  const prog = clamp01((t - sub.start) / Math.max(0.001, sub.end - sub.start));
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.fillRect(x0 + u * 0.012, y0 + boxH - Math.round(u * 0.006), (boxW - u * 0.024) * prog, Math.round(u * 0.004));
  ctx.restore();
  return boxH + pad * 0.3;
}

/**
 * 한 줄을 단어별로 시차를 두고 띄운다 (아래에서 떠오르며). since 는 이 줄이 뜨기 시작한 뒤 지난 시간.
 * 돌려주는 값: 마지막 단어까지 다 뜨는 데 걸리는 시간.
 */
function drawStaggeredLine(ctx, line, x, y, since, { align = 'left', stagger = 0.07, rise = 0 }) {
  const words = line.split(' ');
  const space = ctx.measureText(' ').width;
  const widths = words.map((w) => ctx.measureText(w).width);
  const total = widths.reduce((n, w) => n + w, 0) + space * (words.length - 1);
  let cx = align === 'center' ? x - total / 2 : x;
  ctx.textAlign = 'left';
  words.forEach((w, i) => {
    const a = easeOut(clamp01((since - i * stagger) / 0.35));
    if (a > 0) {
      ctx.save();
      ctx.globalAlpha *= a;
      ctx.fillText(w, cx, y + (1 - a) * rise);
      ctx.restore();
    }
    cx += widths[i] + space;
  });
  return (words.length - 1) * stagger + 0.35;
}

/** 특징 장면의 포인트 칩들. 나레이션 길이에 맞춰 하나씩 팝업. 돌려주는 값: 차지한 높이 */
function drawPointChips(ctx, points, t, scene, x, yBottom, maxW, u) {
  if (!points.length) return 0;
  ctx.save();
  ctx.font = `600 ${Math.round(u * 0.034)}px ${FONT_STACK}`;
  const chipH = Math.round(u * 0.058);
  const gap = Math.round(u * 0.014);
  const padX = Math.round(u * 0.02);
  // 줄 배치 (오른쪽이 넘치면 다음 줄)
  const rows = [[]];
  let cx = 0;
  for (const p of points) {
    const w = ctx.measureText(p).width + padX * 2 + chipH * 0.55;
    if (cx + w > maxW && rows[rows.length - 1].length) { rows.push([]); cx = 0; }
    rows[rows.length - 1].push({ text: p, w });
    cx += w + gap;
  }
  const totalH = rows.length * chipH + (rows.length - 1) * gap;
  // 등장 시각: 나레이션 구간에 고르게. 나레이션이 없으면 0.3초 간격
  const span = scene.narrSec > 1 ? scene.narrSec - 0.8 : points.length * 0.3;
  let idx = 0;
  let y = yBottom - totalH;
  for (const row of rows) {
    let rx = x;
    for (const chip of row) {
      const at = scene.start + scene.textDelay + 0.5 + (span * idx) / Math.max(1, points.length);
      const a = clamp01((t - at) / 0.32);
      if (a > 0) {
        const s = overshoot(a);
        ctx.save();
        ctx.globalAlpha *= Math.min(1, a * 1.5);
        ctx.translate(rx, y + chipH / 2);
        ctx.scale(s, s);
        ctx.translate(-rx, -(y + chipH / 2));
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        roundRect(ctx, rx, y, chip.w, chipH, chipH / 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();
        // 체크 표시
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = Math.max(2, u * 0.004);
        ctx.beginPath();
        ctx.moveTo(rx + padX, y + chipH * 0.52);
        ctx.lineTo(rx + padX + chipH * 0.16, y + chipH * 0.68);
        ctx.lineTo(rx + padX + chipH * 0.42, y + chipH * 0.34);
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(chip.text, rx + padX + chipH * 0.55, y + chipH / 2 + u * 0.002);
        ctx.restore();
      }
      rx += chip.w + gap;
      idx++;
    }
    y += chipH + gap;
  }
  ctx.restore();
  return totalH + Math.round(u * 0.02);
}

/**
 * 장면의 라벨·제목·캡션(+특징 포인트). 제목은 단어별로 시차를 두고 떠오르고, 라벨 칩은 팝, 인트로 제목 밑에는 선이 자란다.
 */
function drawOverlay(ctx, scene, t, width, height, productTitle, showSubtitles) {
  const local = t - scene.start - (scene.textDelay || 0);
  const appear = easeInOut(clamp01(local / 0.5));
  // 마지막 0.4초는 서서히 사라진다
  const vanish = 1 - easeInOut(clamp01((t - scene.start - (scene.duration - 0.4)) / 0.4));
  const a = appear * vanish;
  if (a <= 0) return;

  const u = Math.min(width, height); // 글자 크기는 짧은 변 기준 — 세로 영상에서도 같은 느낌이 나게
  const pad = Math.round(u * 0.06);

  // 하단 어둡게 (글자 가독성)
  const g = ctx.createLinearGradient(0, height * 0.35, 0, height);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.72)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);

  const subtitleH = showSubtitles ? drawSubtitle(ctx, scene, t, width, height, u, pad) : 0;

  ctx.save();
  ctx.globalAlpha = vanish;
  const centered = scene.align === 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 12;

  if (centered) {
    // 인트로·마무리: 화면 가운데 큰 제목, 단어별 등장, 밑에 자라는 선
    ctx.fillStyle = '#ffffff';
    ctx.font = `700 ${Math.round(u * 0.095)}px ${FONT_STACK}`;
    const titleLines = wrapText(ctx, scene.title, width - pad * 2);
    const lh = Math.round(u * 0.115);
    let y = height / 2 - ((titleLines.length - 1) * lh) / 2 - (subtitleH ? u * 0.03 : 0);
    let since = local - 0.1;
    for (const line of titleLines) {
      const took = drawStaggeredLine(ctx, line, width / 2, y, since, { align: 'center', stagger: 0.09, rise: u * 0.04 });
      since -= took * 0.6;
      y += lh;
    }
    // 선
    const lineP = easeOut(clamp01((local - 0.4) / 0.6));
    if (lineP > 0) {
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fbbf24';
      const lw = u * 0.12 * lineP;
      ctx.fillRect(width / 2 - lw / 2, y - lh + Math.round(u * 0.035), lw, Math.max(2, u * 0.005));
      ctx.shadowBlur = 12;
    }
    if (scene.caption) {
      ctx.font = `400 ${Math.round(u * 0.042)}px ${FONT_STACK}`;
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      const capLines = wrapText(ctx, scene.caption, width - pad * 2);
      y += Math.round(u * 0.04);
      const ca = easeOut(clamp01((local - 0.55) / 0.4));
      ctx.globalAlpha = vanish * ca;
      ctx.textAlign = 'center';
      for (const line of capLines) { ctx.fillText(line, width / 2, y + (1 - ca) * u * 0.02); y += Math.round(u * 0.06); }
    }
  } else {
    const points = scene.kind === 'feature' ? (scene.points || []) : [];
    const capFont = `400 ${Math.round(u * 0.038)}px ${FONT_STACK}`;
    const titleFont = `700 ${Math.round(u * 0.075)}px ${FONT_STACK}`;
    ctx.font = capFont;
    // 특징 장면에서 포인트 칩이 있으면 캡션은 생략 (같은 말이 두 번 나온다)
    const capLines = scene.caption && !points.length ? wrapText(ctx, scene.caption, width - pad * 2) : [];
    ctx.font = titleFont;
    const titleLines = wrapText(ctx, scene.title, width - pad * 2);
    const capLh = Math.round(u * 0.052);
    const titleLh = Math.round(u * 0.09);

    // 아래에서부터 쌓아 올린다: (자막 띠) → 포인트 칩 → 캡션 → 제목 → 라벨 칩
    let y = height - pad - subtitleH;
    ctx.shadowBlur = 0;
    const chipsH = drawPointChips(ctx, points, t, scene, pad, y, width - pad * 2, u);
    y -= chipsH;
    ctx.shadowBlur = 12;
    ctx.font = capFont;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    const ca = easeOut(clamp01((local - 0.35) / 0.4));
    ctx.save();
    ctx.globalAlpha = vanish * ca;
    ctx.textAlign = 'left';
    for (let i = capLines.length - 1; i >= 0; i--) { ctx.fillText(capLines[i], pad, y + (1 - ca) * u * 0.02); y -= capLh; }
    ctx.restore();
    if (capLines.length) y -= Math.round(u * 0.012);
    ctx.font = titleFont;
    ctx.fillStyle = '#ffffff';
    // 제목은 아래 줄부터 자리를 잡고, 위 줄부터 순서대로 뜬다
    const titleTop = y - (titleLines.length - 1) * titleLh;
    let since = local - 0.08;
    for (let i = 0; i < titleLines.length; i++) {
      const took = drawStaggeredLine(ctx, titleLines[i], pad, titleTop + i * titleLh, since, { stagger: 0.07, rise: u * 0.035 });
      since -= took * 0.6;
    }
    y = titleTop - titleLh;
    if (scene.label) {
      y -= Math.round(u * 0.005);
      ctx.shadowBlur = 0;
      ctx.font = `600 ${Math.round(u * 0.03)}px ${FONT_STACK}`;
      const chipH = Math.round(u * 0.05);
      const chipW = ctx.measureText(scene.label).width + chipH;
      const pop = overshoot(clamp01(local / 0.35));
      const cy = y - chipH / 2 + Math.round(u * 0.008);
      ctx.save();
      ctx.translate(pad, cy);
      ctx.scale(pop, pop);
      ctx.translate(-pad, -cy);
      ctx.fillStyle = scene.kind === 'feature' ? 'rgba(217,119,6,0.95)' : 'rgba(37,99,235,0.92)';
      roundRect(ctx, pad, y - chipH + Math.round(u * 0.008), chipW, chipH, chipH / 2);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(scene.label, pad + chipH / 2, cy);
      ctx.restore();
    }
    // 우상단 상품명 (작게)
    if (productTitle) {
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      ctx.shadowBlur = 8;
      ctx.font = `500 ${Math.round(u * 0.03)}px ${FONT_STACK}`;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(productTitle, width - pad, pad * 0.7);
    }
  }
  ctx.restore();
}

/** 시각 t 에 화면에 보이는 장면과 샷을 찾는다 */
function locate(timeline, t) {
  const idx = timeline.findIndex((s) => t < s.end);
  const sceneIdx = idx === -1 ? timeline.length - 1 : idx;
  const scene = timeline[sceneIdx];
  const currentShot = scene.shots.find((sh) => t < sh.end) || scene.shots[scene.shots.length - 1];
  const shotIdx = scene.shots.indexOf(currentShot);
  const under = shotIdx > 0 ? scene.shots[shotIdx - 1] : timeline[sceneIdx - 1]?.shots?.slice(-1)[0];
  return { scene, sceneIdx, currentShot, under, prev: timeline[sceneIdx - 1] };
}

/**
 * 시각 t 에 화면에 보이는 클립(현재 샷, 전환 중이면 직전 샷까지)을 그 시각으로 맞춘다.
 * drawFrame 은 동기 함수라 그림 그리기 전에 이걸 먼저 await 해야 한다.
 */
async function seekClipsForTime(timeline, t) {
  const { currentShot, under } = locate(timeline, t);
  if (currentShot.clip) await seekVideo(currentShot.clip.video, t - currentShot.start);
  if (t - currentShot.start < (currentShot.transSec || 0) && under?.clip) await seekVideo(under.clip.video, under.end - under.start - 0.001);
}

/** 캔버스 변환을 걸고 샷을 그린다 (푸시·줌스루용) */
function drawShotTransformed(ctx, shot, t, width, height, alpha, palette, look, { dx = 0, scale = 1 }) {
  ctx.save();
  if (dx) ctx.translate(dx, 0);
  if (scale !== 1) {
    ctx.translate(width / 2, height / 2);
    ctx.scale(scale, scale);
    ctx.translate(-width / 2, -height / 2);
  }
  drawShot(ctx, shot, t, width, height, alpha, palette, look);
  ctx.restore();
}

/**
 * 시각 t 의 한 프레임을 그린다.
 * 샷 경계에서는 그 샷의 transition 으로 직전 샷과 잇는다: fade(겹쳐 페이드) / push(옆으로 밀기) / zoom(줌스루) / dip(검게 갔다 옴).
 */
function drawFrame(ctx, project, t) {
  const { width, height, timeline, productTitle, showSubtitles } = project;
  const look = lookOf(project.look);
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);
  const { scene, sceneIdx, currentShot, under, prev } = locate(timeline, t);
  const palette = PALETTES[scene.index % PALETTES.length];
  const underPalette = PALETTES[(prev?.index ?? scene.index) % PALETTES.length];
  const transSec = currentShot.transSec || 0;
  const q = transSec > 0 ? clamp01((t - currentShot.start) / transSec) : 1;
  const underT = under ? Math.min(t, under.end - 0.001) : 0;

  if (q >= 1 || !under) {
    drawShot(ctx, currentShot, t, width, height, 1, palette, look);
  } else {
    const e = easeInOut(q);
    switch (currentShot.transition) {
      case 'push': {
        const dir = sceneIdx % 2 === 0 ? 1 : -1; // 번갈아 왼쪽/오른쪽으로
        drawShotTransformed(ctx, under, underT, width, height, 1, underPalette, look, { dx: -dir * e * width });
        drawShotTransformed(ctx, currentShot, t, width, height, 1, palette, look, { dx: dir * (1 - e) * width });
        break;
      }
      case 'zoom':
        drawShotTransformed(ctx, under, underT, width, height, 1 - e, underPalette, look, { scale: 1 + 0.35 * e });
        drawShotTransformed(ctx, currentShot, t, width, height, e, palette, look, { scale: 1.2 - 0.2 * e });
        break;
      case 'dip':
        if (q < 0.5) {
          drawShot(ctx, under, underT, width, height, 1, underPalette, look);
          ctx.fillStyle = `rgba(0,0,0,${easeInOut(q * 2)})`;
          ctx.fillRect(0, 0, width, height);
        } else {
          drawShot(ctx, currentShot, t, width, height, 1, palette, look);
          ctx.fillStyle = `rgba(0,0,0,${1 - easeInOut((q - 0.5) * 2)})`;
          ctx.fillRect(0, 0, width, height);
        }
        break;
      default: // fade
        drawShot(ctx, under, underT, width, height, 1, underPalette, look);
        drawShot(ctx, currentShot, t, width, height, e, palette, look);
    }
  }
  drawGrade(ctx, project, t, look);
  drawOverlay(ctx, scene, t, width, height, scene.align === 'center' ? '' : productTitle, showSubtitles);
}

// ── 프로젝트 준비 ────────────────────────────────────────────────

/**
 * 장면 재료를 모두 준비해 "프로젝트" 를 만든다. 미리보기와 인코딩이 같은 프로젝트를 쓴다.
 *
 * @param {object} p
 * @param {string} p.productTitle     우상단에 작게 들어가는 상품명
 * @param {Array}  p.scenes           [{ label, title, caption, narration, align:'bottom'|'center', imageUrls:string[],
 *                                       kind?:'intro'|'route'|'feature'|'day'|'closing', points?:string[], dayNumbers?:number[],
 *                                       audio:{samples:Float32Array, sampleRate}|null, videoBlobs?:Blob[] }]
 *                                       videoBlobs 가 있으면 사진 대신 그 클립들을 깐다(Veo 결과나 사용자 동영상, mp4/webm).
 *                                       여럿이면 문장 경계에서 컷. 옛 이름 videoBlob(하나)도 받는다.
 *                                       kind 는 전환·레이아웃을 정한다(없으면 첫 장면 intro, 마지막 closing, 나머지 day).
 *                                       points 는 특징 장면에 칩으로 뜨는 포인트. dayNumbers 는 미니맵 전환의 구간을 찾는 데 쓴다.
 * @param {Array}  [p.route]          [{ name, lat, lng, day }] 방문 순서. 2곳 이상이면 경로 장면(kind 'route')은 지도로 그리고,
 *                                       일차 장면 앞에 미니맵 전환이 붙는다.
 * @param {string} [p.googleMapsKey]  있으면 Google Static Maps 로 지도 배경을 받는다. 없거나 실패하면 추상 약도.
 * @param {'16:9'|'9:16'|'1:1'} [p.aspect='16:9']
 * @param {'calm'|'energetic'|'premium'} [p.look='calm']  색감(비네팅·틴트·입자). 대본 톤과 같은 이름
 * @param {boolean} [p.showSubtitles=true]   나레이션 자막 띠
 * @param {number} [p.narrationRate=1]       나레이션 속도 (1.1 = 10% 빠르게, 음정 유지)
 * @param {{ mode:'auto'|'file'|'none', mood?:string, gain?:number, samples?:Float32Array, duck?:boolean }} [p.music]
 *                                           배경음악. auto 는 톤(mood)에 맞춰 합성, file 은 samples(48k mono) 사용.
 *                                           duck(기본 true)은 나레이션 중 음악을 낮추는 덕킹. 끄면 음악이 일정한 음량으로 깔린다
 * @param {boolean} [p.sfx=true]             장면 전환 효과음
 * @param {Map} [p.imageCache]               loadImage 캐시 (여러 형식을 뽑을 때 사진을 다시 받지 않게)
 * @param {boolean} [p.decodeClipAudio=true]  클립 소리를 디코딩해 섞을지. 썸네일처럼 프레임 하나만 필요할 때 false
 * @param {(info:{stage:string, done:number, total:number})=>void} [p.onProgress]
 * @param {AbortSignal} [p.signal]
 */
export async function prepareProject({ productTitle, scenes, aspect = '16:9', look = 'calm', showSubtitles = true, narrationRate = 1, music = { mode: 'auto', mood: 'calm', gain: 1 }, sfx = true, route, googleMapsKey, imageCache, decodeClipAudio = true, onProgress, signal }) {
  if (!Array.isArray(scenes) || !scenes.length) throw new Error('장면이 없습니다.');
  const { width, height } = ASPECTS[aspect] || ASPECTS['16:9'];
  const u = Math.min(width, height);
  // 경로 지도 (배경 이미지는 비율마다 따로 받는다 — imageCache 가 URL 로 캐시하므로 같은 비율은 한 번만)
  const map = await buildRouteMap({ route, width, height, googleMapsKey, imageCache, loadImage });
  // 자막 한 조각의 글자 수 상한: 가로 화면은 넉넉히, 세로는 짧게
  const subtitleMaxChars = Math.max(12, Math.round((width / (u * 0.036)) * 0.55));

  const prepared = [];
  for (let i = 0; i < scenes.length; i++) {
    if (signal?.aborted) throw new DOMException('중단됨', 'AbortError');
    const s = scenes[i];
    const urls = (s.imageUrls || []).filter(Boolean).slice(0, 3);
    // 여기서는 원본(bitmap)만 모은다. 화면 비율로 미리 그리는 건 레이아웃(전체/2분할/콜라주)이 정해진 뒤 칸 크기에 맞춰 한다.
    const images = (await Promise.all(urls.map((url) => loadImage(url, imageCache)))).filter(Boolean);
    let audio = null;
    if (s.audio && s.audio.samples && s.audio.samples.length) {
      // 앞뒤 정적을 잘라내고, 속도를 바꾼 뒤(원래 샘플레이트에서 — WSOLA 는 낮은 레이트가 빠르다) 48k 로 맞춘다
      const trimmed = trimSilence(s.audio.samples, s.audio.sampleRate);
      const paced = timeStretch(trimmed, s.audio.sampleRate, narrationRate);
      audio = { samples: await resampleTo48k(paced, s.audio.sampleRate), sampleRate: AUDIO_RATE };
    }
    const blobs = (Array.isArray(s.videoBlobs) ? s.videoBlobs : s.videoBlob ? [s.videoBlob] : []).filter((b) => b && b.size).slice(0, 3);
    const clips = [];
    for (const b of blobs) {
      try { clips.push(await loadClip(b, { decodeAudio: decodeClipAudio })); } catch { /* 못 여는 클립은 건너뛰고 사진으로 간다 (missingClips 로 알린다) */ }
    }
    const kind = s.kind || (i === 0 ? 'intro' : i === scenes.length - 1 && scenes.length > 1 ? 'closing' : 'day');
    prepared.push({
      index: i,
      kind,
      points: Array.isArray(s.points) ? s.points.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 4) : [],
      dayNumbers: Array.isArray(s.dayNumbers) ? s.dayNumbers.map(Number).filter((n) => n > 0) : [],
      label: s.label || '',
      title: s.title || '',
      caption: s.caption || '',
      narration: s.narration || '',
      align: s.align || 'bottom',
      images,
      missingImages: urls.length - images.length,
      missingClips: blobs.length - clips.length,
      audio,
      clips,
    });
    onProgress?.({ stage: 'prepare', done: i + 1, total: scenes.length });
  }

  const timeline = planTimeline(prepared, subtitleMaxChars, map);
  const durationSec = timeline[timeline.length - 1].end;

  // 샷마다 들어갈 칸 크기에 맞춰 사진을 미리 그려 둔다 (같은 사진·같은 크기는 한 번만)
  const coverCache = new Map();
  const coverFor = (img, w, h, fill) => {
    const key = `${w}x${h}${fill ? 'f' : ''}`;
    let byImg = coverCache.get(img);
    if (!byImg) { byImg = new Map(); coverCache.set(img, byImg); }
    if (!byImg.has(key)) byImg.set(key, prerenderCover(img, Math.round(w), Math.round(h), fill));
    return byImg.get(key);
  };
  for (const s of timeline) {
    for (const shot of s.shots) {
      if (shot.images) {
        const rects = collageRects(shot.images.length, width, height, u);
        shot.images = shot.images.map((img, i) => coverFor(img, rects[i].w, rects[i].h, true));
      } else if (shot.image) {
        shot.image = coverFor(shot.image, width, height);
      }
    }
  }

  // 오디오 트랙: 장면 시작 시각에 맞춰 나레이션(+클립 소리)을 한 트랙으로 합친다
  const totalAudioFrames = Math.ceil(durationSec * AUDIO_RATE);
  const track = new Float32Array(totalAudioFrames);
  for (const s of timeline) {
    const offset = Math.round(s.start * AUDIO_RATE);
    // 클립 자체 소리(바람·물·거리 소음)는 그 샷이 보이는 동안만 낮게 깐다
    for (const shot of s.shots) {
      if (!shot.clip?.audio) continue;
      const a = shot.clip.audio.samples;
      const at = Math.round(shot.start * AUDIO_RATE);
      const n = Math.min(a.length, Math.round((shot.end - shot.start) * AUDIO_RATE), totalAudioFrames - at);
      const fadeOut = Math.min(n, Math.round(AUDIO_RATE * 0.6));
      const fadeIn = Math.min(n, Math.round(AUDIO_RATE * 0.2));
      for (let i = 0; i < n; i++) {
        const env = Math.min(i < fadeIn ? i / fadeIn : 1, i > n - fadeOut ? (n - i) / fadeOut : 1);
        track[at + i] += a[i] * CLIP_BED_GAIN * env;
      }
    }
    if (!s.audio) continue;
    const n = Math.min(s.audio.samples.length, totalAudioFrames - offset);
    for (let i = 0; i < n; i++) track[offset + i] += s.audio.samples[i];
  }
  // 배경음악: 합성하거나 올린 파일을 길이에 맞춰 깔고, 나레이션 구간에서는 낮춘다(덕킹, music.duck === false 면 안 낮춤)
  const musicMode = music?.mode || 'none';
  if (musicMode !== 'none') {
    onProgress?.({ stage: 'music', done: 0, total: 1 });
    let bed = null;
    if (musicMode === 'auto') {
      bed = await generateMusic({ durationSec, mood: music.mood || 'calm', sampleRate: AUDIO_RATE });
    } else if (music.samples && music.samples.length) {
      // 짧으면 반복, 길면 자르고 끝을 2초 페이드아웃
      bed = new Float32Array(totalAudioFrames);
      for (let i = 0; i < totalAudioFrames; i++) bed[i] = music.samples[i % music.samples.length];
      const fo = Math.min(totalAudioFrames, AUDIO_RATE * 2);
      for (let i = 0; i < fo; i++) bed[totalAudioFrames - 1 - i] *= i / fo;
    }
    if (bed) {
      const g = MUSIC_BASE_GAIN * (typeof music.gain === 'number' ? music.gain : 1);
      const n = Math.min(bed.length, totalAudioFrames);
      if (music.duck === false) {
        for (let i = 0; i < n; i++) track[i] += bed[i] * g;
      } else {
        const speech = timeline.filter((s) => s.narrSec > 0).map((s) => ({ start: s.start, end: s.start + s.narrSec }));
        const duck = buildDuckingCurve(totalAudioFrames, AUDIO_RATE, speech, { duckTo: MUSIC_DUCK_TO });
        for (let i = 0; i < n; i++) track[i] += bed[i] * g * duck[i];
      }
    }
    onProgress?.({ stage: 'music', done: 1, total: 1 });
  }

  // 효과음: 푸시·줌스루 전환에 우시. 페이드·딥투블랙은 소리 없이 조용히 넘어가야 전환이 구분된다.
  // 컷보다 살짝 먼저 시작해야 "전환음" 으로 들린다.
  if (sfx && timeline.length > 1) {
    const whoosh = await synthWhoosh(AUDIO_RATE);
    for (const s of timeline.slice(1)) {
      if (s.transition !== 'push' && s.transition !== 'zoom') continue;
      const at = Math.max(0, Math.round((s.start - 0.15) * AUDIO_RATE));
      for (let i = 0; i < whoosh.length && at + i < totalAudioFrames; i++) track[at + i] += whoosh[i] * SFX_GAIN;
    }
  }

  // 마지막 0.5초 페이드아웃 + 넘치는 피크는 부드럽게
  const fadeN = Math.min(totalAudioFrames, Math.round(AUDIO_RATE * 0.5));
  for (let i = 0; i < fadeN; i++) track[totalAudioFrames - 1 - i] *= i / fadeN;
  softClip(track);

  return {
    aspect, width, height, productTitle: productTitle || '', showSubtitles,
    look: LOOKS[look] ? look : 'calm',
    grain: makeGrainTile(),
    map,
    timeline, durationSec, audioTrack: track,
    missingImages: prepared.reduce((n, s) => n + s.missingImages, 0),
    missingClips: prepared.reduce((n, s) => n + s.missingClips, 0),
  };
}

/** 클립용 <video> 와 object URL 을 정리한다 */
export function releaseProject(project) {
  for (const s of project?.timeline || []) {
    for (const clip of s.clips || []) {
      try { clip.video.removeAttribute('src'); clip.video.load(); } catch { /* 무시 */ }
      URL.revokeObjectURL(clip.url);
    }
  }
}

/** 시각 t 의 프레임을 캔버스에 그린다 (클립이 있으면 먼저 그 시각으로 옮긴다) */
export async function renderFrameAt(project, canvas, t) {
  if (canvas.width !== project.width || canvas.height !== project.height) {
    canvas.width = project.width;
    canvas.height = project.height;
  }
  await seekClipsForTime(project.timeline, t);
  drawFrame(canvas.getContext('2d'), project, t);
}

/**
 * 장면 하나의 대표 프레임을 그려 미리보기(dataURL)로 돌려준다. 인코딩 없이 모양만 확인할 때.
 */
export async function renderSceneThumbnail({ scene, productTitle, aspect = '16:9', look = 'calm', route, googleMapsKey, imageCache }) {
  const project = await prepareProject({ productTitle, scenes: [scene], aspect, look, showSubtitles: false, route, googleMapsKey, imageCache, decodeClipAudio: false });
  const canvas = document.createElement('canvas');
  // 글자·칩이 다 떠오른 뒤(1.2초) 모습
  await renderFrameAt(project, canvas, Math.min(1.2, project.durationSec / 2));
  releaseProject(project);
  // 썸네일은 작게
  const out = document.createElement('canvas');
  const scale = 640 / Math.max(project.width, project.height);
  out.width = Math.round(project.width * scale);
  out.height = Math.round(project.height * scale);
  out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
  return out.toDataURL('image/jpeg', 0.85);
}

// ── 미리보기 (실시간 재생) ───────────────────────────────────────

/**
 * 캔버스에서 프로젝트를 소리와 함께 재생한다. 인코딩 없이 결과를 확인하는 용도.
 * 시간 기준은 AudioContext 시계라 소리와 그림이 어긋나지 않는다.
 *
 * @returns {{ play():Promise<void>, pause():void, seek(t:number):Promise<void>, destroy():void, duration:number, onTime(cb:(t:number, playing:boolean)=>void):void }}
 */
export function createPreview(project, canvas) {
  canvas.width = project.width;
  canvas.height = project.height;
  const ctx = canvas.getContext('2d');
  let audioCtx = null;
  let source = null;
  let startedAt = 0; // audioCtx.currentTime 기준 재생 시작 시각
  let offset = 0; // 재생 시작 위치(초)
  let playing = false;
  let raf = 0;
  let drawing = false;
  let timeCb = null;

  const now = () => (playing ? offset + (audioCtx.currentTime - startedAt) : offset);

  async function paint(t) {
    if (drawing) return;
    drawing = true;
    try {
      await seekClipsForTime(project.timeline, t);
      drawFrame(ctx, project, t);
    } finally { drawing = false; }
  }

  async function tick() {
    if (!playing) return;
    const t = now();
    if (t >= project.durationSec) {
      pause();
      offset = 0;
      await paint(0);
      timeCb?.(0, false);
      return;
    }
    await paint(t);
    timeCb?.(t, true);
    raf = requestAnimationFrame(tick);
  }

  function pause() {
    if (!playing) return;
    offset = now();
    playing = false;
    cancelAnimationFrame(raf);
    try { source?.stop(); } catch { /* 이미 멈춤 */ }
    source = null;
    timeCb?.(offset, false);
  }

  async function play() {
    if (playing) return;
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') await audioCtx.resume();
    if (offset >= project.durationSec) offset = 0;
    source = audioCtx.createBufferSource();
    source.buffer = floatToAudioBuffer(audioCtx, project.audioTrack, AUDIO_RATE);
    source.connect(audioCtx.destination);
    source.start(0, offset);
    startedAt = audioCtx.currentTime;
    playing = true;
    tick();
  }

  async function seek(t) {
    const wasPlaying = playing;
    pause();
    offset = Math.max(0, Math.min(t, project.durationSec));
    await paint(offset);
    timeCb?.(offset, false);
    if (wasPlaying) await play();
  }

  function destroy() {
    pause();
    try { audioCtx?.close(); } catch { /* 무시 */ }
    audioCtx = null;
  }

  paint(0);
  return {
    play, pause, seek, destroy,
    duration: project.durationSec,
    onTime(cb) { timeCb = cb; },
  };
}

// ── 인코딩 ───────────────────────────────────────────────────────

/**
 * 완성 파일이 MAX_OUTPUT_BYTES 안에 들어오는 영상 비트레이트(bps).
 * 파일 크기 ≈ (영상 + 오디오 비트레이트) × 길이 / 8 이므로 거꾸로 푼다. mp4 컨테이너·키프레임 여유로 8% 를 남긴다.
 * @param {number} durationSec
 * @param {number} audioBitrate  bps
 */
export function videoBitrateFor(durationSec, audioBitrate = 96_000) {
  const budgetBits = MAX_OUTPUT_BYTES * 8 * 0.92 - audioBitrate * durationSec;
  const fit = Math.floor(budgetBits / Math.max(1, durationSec));
  return Math.max(VIDEO_BITRATE_MIN, Math.min(VIDEO_BITRATE_MAX, fit));
}

async function pickVideoConfig(width, height, fps, bitrate) {
  // High 4.0 → Main 3.1 → Baseline 3.1 순서. 720p30 은 3.1 로도 충분하다.
  const candidates = ['avc1.640028', 'avc1.4d001f', 'avc1.42001f'];
  for (const codec of candidates) {
    // bitrateMode 'constant': 크기 상한을 지키려면 평균이 아니라 일정한 비트레이트가 필요하다 (VBR 은 움직임 많은 클립에서 튄다).
    const config = { codec, width, height, bitrate, bitrateMode: 'constant', framerate: fps, avc: { format: 'avc' }, latencyMode: 'quality' };
    try {
      const { supported } = await VideoEncoder.isConfigSupported(config);
      if (supported) return config;
    } catch { /* 다음 후보 */ }
  }
  throw new Error('이 브라우저는 H.264 영상 인코딩을 지원하지 않습니다. 최신 Chrome 또는 Edge 에서 시도해 주세요.');
}

async function pickAudioConfig() {
  // AAC 가 mp4 의 표준이지만 플랫폼 인코더가 없으면(리눅스 등) Opus 로 대신한다.
  const candidates = [
    { muxCodec: 'aac', config: { codec: 'mp4a.40.2', sampleRate: AUDIO_RATE, numberOfChannels: 1, bitrate: 96_000 } },
    { muxCodec: 'opus', config: { codec: 'opus', sampleRate: AUDIO_RATE, numberOfChannels: 1, bitrate: 64_000 } },
  ];
  for (const c of candidates) {
    try {
      const { supported } = await AudioEncoder.isConfigSupported(c.config);
      if (supported) return c;
    } catch { /* 다음 후보 */ }
  }
  throw new Error('이 브라우저는 오디오 인코딩을 지원하지 않습니다. 최신 Chrome 또는 Edge 에서 시도해 주세요.');
}

/**
 * 프로젝트를 mp4 로 인코딩한다. 완성 파일은 MAX_OUTPUT_BYTES(20MB) 를 넘지 않게 한다:
 * 길이에 맞춰 비트레이트를 정하고, 그래도 넘치면(인코더가 목표를 못 지킨 경우) 비트레이트를 실제 초과 비율만큼 낮춰 한 번 더 만든다.
 * @returns {Promise<{ blob: Blob, durationSec: number, audioCodec: string, aspect: string, width: number, height: number, videoBitrate: number }>}
 */
export async function encodeProject(project, { onProgress, signal, videoBitrate } = {}) {
  if (!isVideoBuildSupported()) {
    throw new Error('이 브라우저는 영상 조립(WebCodecs)을 지원하지 않습니다. 최신 Chrome 또는 Edge 에서 시도해 주세요.');
  }
  const audioPick = await pickAudioConfig();
  // videoBitrate: 호출자가 정한 영상 비트레이트(예: AI 에 보낼 목업을 작게) — 없으면 길이로 정한다
  let bitrate = videoBitrate || videoBitrateFor(project.durationSec, audioPick.config.bitrate);
  let result = await encodeOnce(project, audioPick, bitrate, { onProgress, signal });
  if (result.blob.size > MAX_OUTPUT_BYTES && bitrate > VIDEO_BITRATE_MIN) {
    // 초과한 만큼 줄이되 조금 더(0.95) 여유를 둔다. 오디오 몫은 그대로라 영상 몫만 계산한다.
    const audioBytes = (audioPick.config.bitrate * project.durationSec) / 8;
    const ratio = Math.max(0.3, ((MAX_OUTPUT_BYTES - audioBytes) / Math.max(1, result.blob.size - audioBytes)) * 0.95);
    bitrate = Math.max(VIDEO_BITRATE_MIN, Math.floor(bitrate * ratio));
    result = await encodeOnce(project, audioPick, bitrate, { onProgress, signal });
  }
  return result;
}

async function encodeOnce(project, audioPick, bitrate, { onProgress, signal } = {}) {
  const { width, height, timeline, durationSec, audioTrack } = project;
  const fps = VIDEO_FPS;
  const totalFrames = Math.ceil(durationSec * fps);

  const videoConfig = await pickVideoConfig(width, height, fps, bitrate);

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width, height, frameRate: fps },
    audio: { codec: audioPick.muxCodec, numberOfChannels: 1, sampleRate: AUDIO_RATE },
    fastStart: 'in-memory', // moov 를 앞에 두어야 <video> 가 다 받기 전에 재생을 시작한다
  });

  let encodeError = null;
  const videoEncoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => { encodeError = e; },
  });
  videoEncoder.configure(videoConfig);
  const audioEncoder = new AudioEncoder({
    output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
    error: (e) => { encodeError = e; },
  });
  audioEncoder.configure(audioPick.config);

  try {
    const CHUNK = AUDIO_RATE / 10; // 0.1초씩
    for (let pos = 0; pos < audioTrack.length; pos += CHUNK) {
      if (signal?.aborted) throw new DOMException('중단됨', 'AbortError');
      const n = Math.min(CHUNK, audioTrack.length - pos);
      const data = new AudioData({
        format: 'f32',
        sampleRate: AUDIO_RATE,
        numberOfFrames: n,
        numberOfChannels: 1,
        timestamp: Math.round((pos / AUDIO_RATE) * 1e6),
        data: audioTrack.subarray(pos, pos + n),
      });
      audioEncoder.encode(data);
      data.close();
    }

    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    const frameDur = Math.round(1e6 / fps);
    for (let i = 0; i < totalFrames; i++) {
      if (signal?.aborted) throw new DOMException('중단됨', 'AbortError');
      if (encodeError) throw encodeError;
      // 인코더 큐가 밀리면 잠깐 기다린다 (메모리 폭주 방지)
      while (videoEncoder.encodeQueueSize > 8) {
        await new Promise((r) => setTimeout(r, 4));
        if (encodeError) throw encodeError;
      }
      if (project.renderAt) {
        await project.renderAt(ctx, i / fps); // 장면 조립이 아닌 단순 이어붙이기(concatClips)
      } else {
        await seekClipsForTime(timeline, i / fps);
        drawFrame(ctx, project, i / fps);
      }
      const frame = new VideoFrame(canvas, { timestamp: i * frameDur, duration: frameDur });
      videoEncoder.encode(frame, { keyFrame: i % (fps * 2) === 0 });
      frame.close();
      if (i % 5 === 0) {
        onProgress?.({ stage: 'encode', done: i + 1, total: totalFrames });
        // UI 가 얼어붙지 않게 잠깐 양보
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    onProgress?.({ stage: 'encode', done: totalFrames, total: totalFrames });

    await videoEncoder.flush();
    await audioEncoder.flush();
    if (encodeError) throw encodeError;
    muxer.finalize();
  } finally {
    try { videoEncoder.close(); } catch { /* 이미 닫힘 */ }
    try { audioEncoder.close(); } catch { /* 이미 닫힘 */ }
  }

  return {
    blob: new Blob([muxer.target.buffer], { type: 'video/mp4' }),
    durationSec,
    audioCodec: audioPick.muxCodec,
    aspect: project.aspect,
    width,
    height,
    videoBitrate: bitrate,
  };
}

// ── 목업 영상 → AI 동영상 (MockupVideoModal) ─────────────────────────

/**
 * mp4 Blob 에서 지정한 시각들의 프레임을 뽑는다.
 *   thumb      — AI 분석용 작은 JPEG(긴 변 thumbSize)
 *   firstFrame — aspect 를 주면 Veo 첫 프레임용 JPEG(출력 비율로 가운데 crop-fill, makeFirstFrameBase64 와 같은 규칙)
 * 둘 다 base64(data: 접두사 없음). Blob 에서 읽으므로 캔버스가 오염(CORS)되지 않는다.
 * @returns {Promise<{ duration:number, width:number, height:number, frames:Array<{ t:number, thumb:string, firstFrame?:string }> }>}
 */
export async function grabFrames(blob, times, { thumbSize = 448, aspect } = {}) {
  const clip = await loadClip(blob, { decodeAudio: false });
  try {
    const { video, duration } = clip;
    const vw = video.videoWidth, vh = video.videoHeight;
    const toB64 = (canvas, q) => { const u = canvas.toDataURL('image/jpeg', q); return u.slice(u.indexOf(',') + 1); };
    const frames = [];
    for (const t of times) {
      await seekVideo(video, t);
      const scale = Math.min(1, thumbSize / Math.max(vw, vh));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(vw * scale)); c.height = Math.max(1, Math.round(vh * scale));
      c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
      const frame = { t, thumb: toB64(c, 0.7) };
      if (aspect) {
        const { width, height } = ASPECTS[aspect] || ASPECTS['16:9'];
        const f = document.createElement('canvas');
        f.width = width; f.height = height;
        drawClipCover(f.getContext('2d'), clip, 0, 0, width, height);
        frame.firstFrame = toB64(f, 0.85);
      }
      frames.push(frame);
    }
    return { duration, width: vw, height: vh, frames };
  } finally {
    clip.video.removeAttribute('src');
    URL.revokeObjectURL(clip.url);
  }
}

/**
 * 클립들을 순서대로 이어 붙여 mp4 하나로 만든다 (장면마다 앞에서 seconds 만큼만 쓴다, 하드 컷).
 * 소리: 'clips' = 각 클립 자체 소리(Veo 현장음) / 'mockup' = audioBlob(목업 영상)의 소리를 장면마다 srcStart 초부터 / 'none' = 무음.
 *   목업 소리를 장면 단위로 붙이는 이유: 클립이 계획보다 짧게 오면(VEO_MAX_SECONDS 등) 통째로 깔았을 때 그 뒤로 화면과 소리가 어긋난다.
 * 크기 상한(MAX_OUTPUT_BYTES)은 encodeProject 가 지킨다.
 * @param {Array<{ blob:Blob, seconds:number, srcStart?:number }>} segments  srcStart: 이 장면이 목업의 몇 초부터인지 ('mockup' 소리용)
 * @returns {Promise<{ blob:Blob, durationSec:number, width:number, height:number }>}
 */
export async function concatClips(segments, { aspect = '16:9', audio = 'clips', audioBlob, videoBitrate, onProgress, signal } = {}) {
  const { width, height } = ASPECTS[aspect] || ASPECTS['16:9'];
  const clips = [];
  try {
    let start = 0;
    for (const seg of segments) {
      const clip = await loadClip(seg.blob, { decodeAudio: audio === 'clips' });
      const seconds = Math.max(0.5, Math.min(Number(seg.seconds) || clip.duration, clip.duration));
      clips.push({ ...clip, start, seconds, srcStart: Number(seg.srcStart) || 0 });
      start += seconds;
    }
    const durationSec = start;
    const audioTrack = new Float32Array(Math.ceil(durationSec * AUDIO_RATE));
    const fade = Math.round(AUDIO_RATE * 0.02); // 컷마다 20ms 페이드 — 딸깍 소리 방지
    const place = (samples, offsetSec, maxSec, fromSec = 0) => {
      const at = Math.round(offsetSec * AUDIO_RATE);
      const from = Math.round(fromSec * AUDIO_RATE);
      const n = Math.max(0, Math.min(samples.length - from, Math.round(maxSec * AUDIO_RATE), audioTrack.length - at));
      for (let i = 0; i < n; i++) {
        const g = Math.min(1, i / fade, (n - 1 - i) / fade);
        audioTrack[at + i] = samples[from + i] * g;
      }
    };
    if (audio === 'clips') {
      for (const c of clips) if (c.audio) place(c.audio.samples, c.start, c.seconds);
    } else if (audio === 'mockup' && audioBlob) {
      const src = await loadClip(audioBlob, { decodeAudio: true });
      src.video.removeAttribute('src');
      URL.revokeObjectURL(src.url);
      if (src.audio) for (const c of clips) place(src.audio.samples, c.start, c.seconds, c.srcStart);
    }

    const project = {
      width, height, aspect, durationSec, audioTrack,
      renderAt: async (ctx, t) => {
        let c = clips[clips.length - 1];
        for (const x of clips) { if (t < x.start + x.seconds) { c = x; break; } }
        await seekVideo(c.video, t - c.start);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        drawClipCover(ctx, c, 0, 0, width, height);
      },
    };
    return await encodeProject(project, { onProgress, signal, videoBitrate });
  } finally {
    for (const c of clips) { c.video.removeAttribute('src'); URL.revokeObjectURL(c.url); }
  }
}
